import styles from './SuggestedSetup.module.scss';

import React, { FC } from 'react';
import cx from 'classnames';

import { AppType, toEntry } from 'services/cms/typesUtil';
import { FadeToastUp } from 'components/Animation/FadeToastUp/FadeToastUp';
import { ColorTheme, SuggestedSetup, TextPosition } from 'api/contentful/contentTypes.cloverdotcom';
import { TextV2 } from 'components/TextV2/TextV2';
import { ButtonLinkV2 } from 'components/Button/ButtonLinkV2';
import { FlatEntry } from 'services/cms/flattenContentful';
import { SubjectMedia } from 'components/SubjectMedia/SubjectMedia';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { RichBlurb } from 'components/RichBlurb/RichBlurb';

export type SuggestedSetupProps = AppType.Component<FlatEntry<SuggestedSetup>>;

export const SuggestedSetupModule: FC<SuggestedSetupProps> = ({
  internalName,
  headingTagName,
  textPosition = TextPosition.LEFT,
  theme = ColorTheme.GREEN,
  topic,
  suggestionTitle,
  suggestionValue,
  _attributes,
}) => {
  const { browser } = useBreakpoints();
  if (!topic) return null;

  const { title, subTitle: eyebrow, richBlurb, link, secondaryLinks = [] } = topic;
  const { mobile: isMobile, desktop: isDesktop } = browser.is;
  const links = [link, ...secondaryLinks].map((l, i) => (
    <ButtonLinkV2 key={i} isFullWidth={isMobile} entry={toEntry(l)} hideTitle />
  ));
  const blurbAndCtas = (
    <>
      <RichBlurb document={richBlurb} className={styles.blurb} />
      {Boolean(links.length) && <div className={styles.linksContainer}>{links}</div>}
    </>
  );

  return (
    <section
      id={internalName}
      className={cx(styles.suggestedSetupWrapper, {
        [styles.textPositionRight]: textPosition === TextPosition.RIGHT,
        [styles.themePurple]: theme === ColorTheme.PURPLE,
      })}
      {..._attributes}
    >
      <FadeToastUp>
        <div className={styles.textContainer}>
          <TextV2 isMarkdown className={styles.eyebrow} variant="modern-eyebrow" value={eyebrow} />
          <TextV2 className={styles.title} variant="modern-h3" value={title} tagName={headingTagName} />
          {!isMobile && blurbAndCtas}
        </div>
      </FadeToastUp>
      <FadeToastUp>
        <>
          <div className={styles.mediaContainer}>
            <SubjectMedia entry={topic} className={styles.media} playWhenInView loop />
            {(suggestionTitle || suggestionValue) && (
              <div className={styles.suggestionBanner}>
                <TextV2
                  className={styles.suggestedSetupTitle}
                  tagName="p"
                  variant="modern-body-medium"
                  value={suggestionTitle}
                />
                <TextV2
                  className={styles.suggestedSetupDescription}
                  isMarkdown
                  tagName="div"
                  variant={isDesktop ? 'modern-body-large-medium' : 'modern-body'}
                  value={suggestionValue}
                />
              </div>
            )}
          </div>
          {isMobile && blurbAndCtas}
        </>
      </FadeToastUp>
    </section>
  );
};

export default SuggestedSetupModule;
